import React from 'react';
import {
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  IconButton,
  Box,
  Chip,
  Card,
  CardContent,
  Typography,
  Stack,
} from '@mui/material';
import { Edit, Plus, Shield } from 'lucide-react';

interface RolesListProps {
  onRoleEdit: (role: any) => void;
  onAddRole: () => void;
  isMobile: boolean;
}

const roles = [
  {
    id: '1',
    name: 'Partner',
    permissions: ['manage_users', 'manage_cases', 'manage_billing', 'view_reports'],
    userCount: 3,
  },
  {
    id: '2',
    name: 'Lawyer',
    permissions: ['manage_cases', 'view_reports'],
    userCount: 8,
  },
  {
    id: '3',
    name: 'Paralegal',
    permissions: ['view_cases', 'manage_documents'],
    userCount: 5,
  },
  {
    id: '4',
    name: 'Client',
    permissions: ['view_own_cases', 'view_own_documents'],
    userCount: 42,
  },
];

export const RolesList: React.FC<RolesListProps> = ({
  onRoleEdit,
  onAddRole,
  isMobile,
}) => {
  const RoleCard = ({ role }: { role: typeof roles[0] }) => (
    <Card sx={{ mb: 2 }}>
      <CardContent>
        <Stack spacing={2}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Box sx={{ 
              p: 1, 
              borderRadius: 1, 
              bgcolor: 'primary.main',
              color: 'primary.contrastText',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}>
              <Shield size={20} />
            </Box>
            <Box sx={{ flex: 1 }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 500 }}>
                {role.name}
              </Typography>
              <Chip
                label={`${role.userCount} users`}
                size="small"
                sx={{ mt: 0.5 }}
              />
            </Box>
            <IconButton
              size="small"
              onClick={() => onRoleEdit(role)}
            >
              <Edit size={18} />
            </IconButton>
          </Box>

          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
            {role.permissions.map((permission) => (
              <Chip
                key={permission}
                label={permission.replace('_', ' ')}
                size="small"
                variant="outlined"
              />
            ))}
          </Box>
        </Stack>
      </CardContent>
    </Card>
  );

  return (
    <>
      <Box sx={{ 
        display: 'flex', 
        justifyContent: 'flex-end', 
        mb: 3,
      }}>
        <Button
          variant="contained"
          startIcon={<Plus />}
          onClick={onAddRole}
          fullWidth={isMobile}
        >
          Add Role
        </Button>
      </Box>

      {isMobile ? (
        <Box>
          {roles.map((role) => (
            <RoleCard key={role.id} role={role} />
          ))}
        </Box>
      ) : (
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Role</TableCell>
                <TableCell>Permissions</TableCell>
                <TableCell>Users</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {roles.map((role) => (
                <TableRow key={role.id} hover>
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Shield size={20} className="text-primary" />
                      {role.name}
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                      {role.permissions.map((permission) => (
                        <Chip
                          key={permission}
                          label={permission.replace('_', ' ')}
                          size="small"
                          variant="outlined"
                        />
                      ))}
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={`${role.userCount} users`}
                      size="small"
                    />
                  </TableCell>
                  <TableCell align="right">
                    <IconButton
                      size="small"
                      onClick={() => onRoleEdit(role)}
                    >
                      <Edit size={18} />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </>
  );
};